import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { rapportini } from '../../api/rapportini';
import type { CorrezioneTrasferta, DettaglioRapportino } from '../../api/rapportini';
import { formattaData, formattaImporto, formattaNumero } from '../../lib/formato';
import { ChiediMotivo } from '../acquisti/ChiediMotivo';

interface Props {
  rapportinoId: string;
  onChiudi: () => void;
  onCambiato: () => void;
}

export function SchedaRapportino({ rapportinoId, onChiudi, onCambiato }: Props) {
  const [rapportino, setRapportino] = useState<DettaglioRapportino | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    let annullato = false;
    setRapportino(null);
    setErrore(null);
    rapportini
      .leggi(rapportinoId)
      .then((r) => !annullato && setRapportino(r))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [rapportinoId]);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  /*  Come nelle altre schede: la risposta del server sostituisce quello che c'è a
      schermo, invece di aggiornarlo qui. Stato e decidibilità li stabilisce il dominio, e
      ricalcolarli nel frontend vorrebbe dire due implementazioni della stessa regola.  */
  async function agisci(azione: () => Promise<DettaglioRapportino>) {
    setInCorso(true);
    setErrore(null);
    try {
      setRapportino(await azione());
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (errore && !rapportino) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!rapportino) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico il rapportino…</p>
      </aside>
    );
  }

  return (
    <CorpoRapportino
      rapportino={rapportino}
      inCorso={inCorso}
      errore={errore}
      onChiudi={onChiudi}
      onDecidi={(decisione) =>
        void agisci(() =>
          decisione.tipo === 'approva'
            ? rapportini.approva(rapportino.id)
            : rapportini.respingi(rapportino.id, decisione.motivo),
        )
      }
      onCorreggiTrasferte={(righe) =>
        void agisci(() => rapportini.correggiTrasferte(rapportino.id, righe))
      }
    />
  );
}

/*  Le trasferte del rapportino, e — finché si può decidere — la loro correzione.

    Le righe non sono solo quelle dichiarate: c'è una riga per ogni commessa della
    giornata, anche senza chilometri. Chi valida sa che il tecnico è passato da un
    cantiere e non l'ha scritto, e deve poterlo aggiungere qui invece di respingere tutto
    il rapportino e aspettare che venga rifatto.

    L'importo non si ricalcola qui mentre si scrive: la formula sta nel dominio, e la
    riga aggiornata torna dal server. Chi corregge vede i chilometri cambiare subito e
    l'importo dopo il salvataggio, che è anche il modo di sapere che è stato salvato.  */
function Trasferte({
  rapportino: r,
  inCorso,
  onCorreggi,
}: {
  rapportino: DettaglioRapportino;
  inCorso: boolean;
  onCorreggi?: (righe: CorrezioneTrasferta[]) => void;
}) {
  const [bozza, setBozza] = useState<Record<string, string>>({});

  // La risposta del server sostituisce la bozza: quello che si vede è quello che è salvato.
  useEffect(() => setBozza({}), [r]);

  const correggibile = r.decidibile && onCorreggi !== undefined;

  const righe = [
    ...r.trasferte.map((t) => ({
      commessaId: t.commessaId,
      commessa: t.commessa,
      km: t.km,
      importo: t.importo as number | null,
    })),
    ...r.ore
      .filter((o) => !r.trasferte.some((t) => t.commessaId === o.commessaId))
      .map((o) => ({ commessaId: o.commessaId, commessa: o.commessa, km: 0, importo: null })),
  ];

  if (righe.length === 0) return null;
  if (r.trasferte.length === 0 && !correggibile) return null;

  const scritto = (commessaId: string, km: number) => bozza[commessaId] ?? (km > 0 ? String(km) : '');

  const cambiate: CorrezioneTrasferta[] = righe
    .filter((riga) => {
      const nuovo = Number(scritto(riga.commessaId, riga.km) || '0');
      return Number.isFinite(nuovo) && nuovo !== riga.km;
    })
    .map((riga) => {
      const nuovo = Number(scritto(riga.commessaId, riga.km) || '0');
      return { commessaId: riga.commessaId, km: nuovo > 0 ? nuovo : null };
    });

  return (
    <section className="trasferte-scheda">
      <table className="confronto">
        <thead>
          <tr>
            <th>Trasferte</th>
            <th className="destra">km</th>
            <th className="destra">Importo</th>
          </tr>
        </thead>
        <tbody>
          {righe.map((riga) => (
            <tr key={riga.commessaId} className={riga.km === 0 ? 'fuori' : ''}>
              <td>
                <span className="nome">{riga.commessa ?? 'commessa non trovata'}</span>
              </td>
              <td className="destra">
                {correggibile ? (
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={1200}
                    step={1}
                    className="km"
                    aria-label={`Chilometri per ${riga.commessa ?? 'commessa non trovata'}`}
                    placeholder="0"
                    value={scritto(riga.commessaId, riga.km)}
                    onChange={(e) =>
                      setBozza((precedente) => ({ ...precedente, [riga.commessaId]: e.target.value }))
                    }
                  />
                ) : (
                  formattaNumero(riga.km)
                )}
              </td>
              <td className="destra">{riga.importo === null ? '—' : formattaImporto(riga.importo)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>
              Totale
              {r.indennitaGiornaliera > 0 && (
                <span className="dettaglio">
                  indennità del giorno {formattaImporto(r.indennitaGiornaliera)}, una sola
                </span>
              )}
            </td>
            <td className="destra">{formattaNumero(r.trasferte.reduce((s, t) => s + t.km, 0))}</td>
            <td className="destra">{formattaImporto(r.trasferteTotali)}</td>
          </tr>
        </tfoot>
      </table>

      {correggibile && cambiate.length > 0 && (
        <div className="azioni">
          <button type="button" disabled={inCorso} onClick={() => onCorreggi!(cambiate)}>
            Salva le trasferte
          </button>
        </div>
      )}
    </section>
  );
}

/** Le due strade, esplicite: chi disegna la scheda non deve sapere come si chiamano le API. */
export type DecisioneRapportino = { tipo: 'approva' } | { tipo: 'respingi'; motivo: string };

/*  Il corpo separato dal caricamento ed esportato, come per la scheda di commessa: si
    disegna con dei dati in mano e si legge quello che esce, senza browser e senza
    database. Qui serve piu' che altrove — i due bottoni devono comparire esattamente sui
    rapportini su cui la decisione e' ancora possibile.  */
export function CorpoRapportino({
  rapportino: r,
  inCorso,
  errore,
  onChiudi,
  onDecidi,
  onCorreggiTrasferte,
}: {
  rapportino: DettaglioRapportino;
  inCorso: boolean;
  errore?: string | null;
  onChiudi: () => void;
  onDecidi: (decisione: DecisioneRapportino) => void;
  onCorreggiTrasferte?: (righe: CorrezioneTrasferta[]) => void;
}) {
  const [chiedeMotivo, setChiedeMotivo] = useState(false);

  return (
    <aside className="scheda" aria-label={`Rapportino del ${formattaData(r.dataLavoro)}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{formattaData(r.dataLavoro)}</strong>
            <span className={`stato stato-${r.stato.toLowerCase()}`}>{r.stato}</span>
            {r.materiali.some((m) => m.daCodificare) && (
              <span className="stato in-ritardo">materiale da codificare</span>
            )}
          </p>
          <h2>{r.operaio ?? 'operaio non in anagrafica'}</h2>
          <p className="riferimenti">
            {formattaNumero(r.oreTotali)} ore
            {r.costoOreOggi !== null && <> · {formattaImporto(r.costoOreOggi)} al costo di oggi</>}
            {r.sincronizzatoIl && <> · arrivato il {formattaData(r.sincronizzatoIl)}</>}
            {r.approvatoIl && (
              <>
                {' '}
                · approvato il {formattaData(r.approvatoIl)}
                {r.approvatoDa && ` da ${r.approvatoDa}`}
              </>
            )}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {r.motivoRespinta && <p className="avviso">Respinto: {r.motivoRespinta}</p>}

      {/*  Il costo orario mancante va detto: senza, le ore di questa persona non entrano
           nel consuntivo come costo, e chi approva sta approvando ore che valgono zero. */}
      {r.costoOrarioOggi === null && (
        <p className="nota">
          {r.operaio ?? 'Questa persona'} non ha un costo orario in anagrafica: queste ore
          entreranno nel consuntivo di commessa senza costo.
        </p>
      )}

      {r.note && <p className="note">{r.note}</p>}

      <table className="righe-richiesta">
        <thead>
          <tr>
            <th>Commessa</th>
            <th className="destra">Ore</th>
          </tr>
        </thead>
        <tbody>
          {r.ore.map((riga) => (
            <tr key={riga.id}>
              <td>
                <span className="descrizione">{riga.commessa ?? 'commessa non trovata'}</span>
                <span className="dettaglio elenco">
                  {riga.cliente && <span>{riga.cliente}</span>}
                  {riga.tipoLavorazione && <span>{riga.tipoLavorazione}</span>}
                  {riga.descrizione && <span>{riga.descrizione}</span>}
                </span>
              </td>
              <td className="destra">{formattaNumero(riga.ore)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Totale</td>
            <td className="destra">{formattaNumero(r.oreTotali)}</td>
          </tr>
        </tfoot>
      </table>

      <Trasferte rapportino={r} inCorso={inCorso} onCorreggi={onCorreggiTrasferte} />

      {r.materiali.length > 0 && (
        <details className="provenienza" open={r.materiali.length <= 6}>
          <summary>
            {r.materiali.length === 1
              ? 'Una riga di materiale'
              : `${r.materiali.length} righe di materiale`}
          </summary>
          {/*  Il materiale del rapportino non è un costo: il costo sta sull'ordine al
               fornitore. Qui dice cosa è stato consumato in cantiere, e quali righe
               l'ufficio deve ancora agganciare a un articolo.  */}
          <p className="nota">
            Consumi dichiarati in cantiere: non entrano nel consuntivo, il costo del
            materiale sta sull'ordine.
          </p>
          <table className="confronto">
            <tbody>
              {r.materiali.map((riga) => (
                <tr key={riga.id} className={riga.daCodificare ? 'fuori' : ''}>
                  <td>
                    <span className="nome">{riga.descrizione}</span>
                    <span className="dettaglio elenco">
                      <span>{riga.commessa ?? 'commessa non trovata'}</span>
                      {riga.siglaProduttore ? (
                        <em className="chiave">
                          {riga.siglaProduttore} {riga.codiceArticoloProduttore}
                        </em>
                      ) : (
                        <em className="da-codificare">da codificare</em>
                      )}
                    </span>
                  </td>
                  <td className="destra">
                    {formattaNumero(riga.quantita)} {riga.unitaMisura}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {errore && <p className="errore">{errore}</p>}

      {chiedeMotivo ? (
        <ChiediMotivo
          domanda="Perché si respinge questo rapportino?"
          etichettaConferma="Respingi"
          inCorso={inCorso}
          onConferma={(motivo) => {
            setChiedeMotivo(false);
            onDecidi({ tipo: 'respingi', motivo });
          }}
          onAnnulla={() => setChiedeMotivo(false)}
        />
      ) : (
        r.decidibile && (
          <div className="azioni">
            <button
              type="button"
              className="primario"
              disabled={inCorso}
              title="Da qui le ore entrano nel consuntivo di commessa"
              onClick={() => onDecidi({ tipo: 'approva' })}
            >
              Approva
            </button>
            <button
              type="button"
              className="pericolo"
              disabled={inCorso}
              onClick={() => setChiedeMotivo(true)}
            >
              Respingi
            </button>
          </div>
        )
      )}
    </aside>
  );
}
